import { Injectable, Inject } from '@angular/core';
import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration
} from '@azure/msal-angular';
import {
  InteractionStatus,
  RedirectRequest,
  EventMessage,
  EventType,
  AccountInfo
} from '@azure/msal-browser';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { filter, takeUntil, map, tap, share } from 'rxjs/operators';

/**
 * Interface representing the structure of token claims
 */
export interface TokenClaims {
  given_name?: string;
  family_name?: string;
  email?: string;
  roles?: string[];
  [key: string]: unknown;
}

/**
 * AuthService handles direct interaction with MSAL libraries
 * This service should only be used by UserProfileService and AppComponent
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly _destroying$ = new Subject<void>();
  private readonly isAuthenticated$ = new BehaviorSubject<boolean>(false);
  private readonly isImpersonatingUser$ = new BehaviorSubject<boolean>(false);

  // Create a shared observable for authentication status changes
  private readonly authStatusChanged$ = this.msalBroadcastService.inProgress$.pipe(
    filter((status: InteractionStatus) => status === InteractionStatus.None),
    tap(() => {
      this.checkAndSetActiveAccount();
      this.updateAuthenticationState();
    }),
    takeUntil(this._destroying$),
    share()
  );

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private readonly msalGuardConfig: MsalGuardConfiguration,
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService
  ) {
    this.initializeAuthListeners();
  }

  /**
   * Initialize listeners for MSAL authentication events
   */
  private initializeAuthListeners(): void {
    // Subscribe to auth status changes to initialize the stream
    this.authStatusChanged$.subscribe();

    // Subscribe to MSAL events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          msg.eventType === EventType.LOGIN_SUCCESS ||
          msg.eventType === EventType.LOGOUT_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result) => {
        if (result.eventType === EventType.LOGIN_SUCCESS) {
          this.checkAndSetActiveAccount();
        }
      });
  }

  /**
   * Returns an observable that emits when authentication status changes
   */
  getAuthStatusChanges(): Observable<boolean> {
    return this.authStatusChanged$.pipe(
      map(() => this.isAuthenticated$.value)
    );
  }

  /**
   * Handles MSAL login redirect
   */
  loginRedirect(): void {
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.msalService.loginRedirect();
    }
  }

  /**
   * Handles MSAL logout redirect
   */
  logoutRedirect(): void {
    this.msalService.logoutRedirect();
    this.updateAuthenticationState(false);
  }

  /**
   * Checks and sets the active account in MSAL
   */
  private checkAndSetActiveAccount(): void {
    let activeAccount = this.msalService.instance.getActiveAccount();
    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  /**
   * Updates the authentication state based on MSAL account info
   */
  private updateAuthenticationState(isAuthenticated?: boolean): void {
    if (isAuthenticated !== undefined) {
      this.isAuthenticated$.next(isAuthenticated);
      return;
    }

    const hasAccounts = this.msalService.instance.getAllAccounts().length > 0;
    this.isAuthenticated$.next(hasAccounts);
  }

  /**
   * Gets the authentication status as an observable
   */
  isAuthenticated(): Observable<boolean> {
    return this.isAuthenticated$.asObservable();
  }

  /**
   * Gets claims from the active account
   */
  getActiveClaims(): TokenClaims | null {
    const activeAccount = this.msalService.instance.getActiveAccount();
    return activeAccount && activeAccount.idTokenClaims ? activeAccount.idTokenClaims as TokenClaims : null;
  }

  /**
   * Gets the active account
   */
  getActiveAccount(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount();
  }

  /**
   * Gets all accounts
   */
  getAllAccounts(): AccountInfo[] {
    return this.msalService.instance.getAllAccounts();
  }

  /**
   * Gets the InteractionStatus observable from MSAL
   */
  getInteractionStatus(): Observable<InteractionStatus> {
    return this.msalBroadcastService.inProgress$;
  }

  /**
   * Toggles user impersonation mode
   * @param isImpersonating Whether to enable impersonation mode
   */
  toggleImpersonation(isImpersonating: boolean): void {
    this.isImpersonatingUser$.next(isImpersonating);
  }

  /**
   * Gets the current impersonation state
   */
  isImpersonatingUser(): Observable<boolean> {
    return this.isImpersonatingUser$.asObservable();
  }

  /**
   * Checks if user has specific roles from token claims
   * When impersonating, Platform.Admin role is filtered out
   */
  hasRoles(roles: string[]): boolean {
    const claims = this.getActiveClaims();
    if (!claims || !claims.roles) {
      return false;
    }

    // If impersonating, filter out Platform.Admin role for role checks
    if (this.isImpersonatingUser$.value) {
      const filteredRoles = (claims.roles as string[]).filter(role => role !== 'Platform.Admin');
      return roles.some(role => filteredRoles.includes(role));
    }

    return roles.some(role => (claims.roles as string[]).includes(role));
  }

  /**
   * Cleanup function to be called on service destruction
   */
  destroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
